import { createContext, useContext } from 'react';
import backend from '../services/backend';
import { useNavigate } from 'react-router-dom';
import { roles, type ApiEndpoints, type Role } from '@avisoma/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';

type Response = ApiEndpoints<string>['/api/auth/me']['GET']['response'];
export type User = Response['user'];

export type UserRole = Role;

const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

interface AuthContextType {
  user: Response['user'] | null;
  server: Response['server'] | null;
  isLoading: boolean;
  error: string | null;
  logout: () => Promise<void>;
  hasAccess: (roles?: UserRole | UserRole[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

// A stored value outside the current role union (for example `tester`, retired in DR-6086) is ignored,
// so an admin's guarded UI does not vanish after a role is removed.
const readStoredEmulatedRole = (): UserRole | null => {
  const stored = localStorage.getItem('emulatedRole');
  return stored !== null && (roles as readonly string[]).includes(stored) ? (stored as UserRole) : null;
};
let emulatedRole: UserRole | null = readStoredEmulatedRole();
const emulateRole = (role: UserRole) => {
  emulatedRole = role;
  localStorage.setItem('emulatedRole', role);
};

// Dev-tooling: expose role switchers on window so admins can flip roles from
// the browser console (`emulateRole.curator()`, etc.). The role is read back
// in `userRole` below.
declare global {
  interface Window {
    emulateRole: Record<UserRole, () => void>;
  }
}
window.emulateRole = {
  supervisor: () => emulateRole('supervisor'),
  admin: () => emulateRole('admin'),
  user: () => emulateRole('user'),
  curator: () => emulateRole('curator'),
  // Machine identity for TRecs v2 (DR-3681). Present so the map stays exhaustive over `Role`;
  // emulating it in the UI shows almost nothing, because a service token's reach is Brain's
  // route allowlist rather than any frontend role gate.
  service: () => emulateRole('service'),
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: meResponse, isLoading, isPending, isFetching, error } = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: () =>
      backend.me().then((res) => {
        backend.setCsrfToken(res.data.csrfToken);
        return res.data;
      }),
    retry: false,
  });

  const userRole =
    ((meResponse?.user?.role as UserRole) === 'admin' ? emulatedRole : null) ?? (meResponse?.user?.role as UserRole);

  const logout = async () => {
    try {
      await backend.logout();
      // Clear every cached response, not only `me`: the next user in this tab must not see this user's data.
      queryClient.clear();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
      throw err;
    }
  };

  if (!meResponse) {
    if (isPending || isFetching) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-gray-500 dark:text-zinc-400">Loading...</div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center p-8">
          <div className="text-red-600 dark:text-red-400 mb-4">Failed to check authentication status</div>
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        user: {
          ...meResponse.user,
          role: userRole,
        },
        server: meResponse.server,
        isLoading,
        error: error?.message ?? null,
        logout,
        hasAccess: (roles?: UserRole | UserRole[]): boolean => {
          const rolesNormalized = (Array.isArray(roles) ? roles : [roles]).filter((i) => !!i);

          return rolesNormalized.length > 0 && rolesNormalized.includes(userRole);
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
